import {useCallback, useEffect, useRef, useState} from 'react';

import {deleteConnection, getConnection} from 'src/openapi/admin/apis/SsoApi';
import {useAppDispatch} from 'src/store/hooks';

import type {ConnectionSettingsResponse} from 'src/openapi/admin/models/ConnectionSettingsResponse';

type UseExistingConnectionReturn = {
    loading: boolean,
    error: string | null,
    connection: ConnectionSettingsResponse | null,
    deleteExisting: () => Promise<boolean>,
    deleting: boolean,
};

export function useExistingConnection(): UseExistingConnectionReturn {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [connection, setConnection] = useState<ConnectionSettingsResponse | null>(null);
    const [deleting, setDeleting] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await dispatch(
                    getConnection({signal: controller.signal}),
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (response.payload?.success) {
                    setConnection(response.payload.data);
                } else {
                    setConnection(null);
                    setError('Failed to load the current SSO connection. Please refresh and try again.');
                }
            } catch (err) {
                if ((err as {name?: string})?.name === 'AbortError') {
                    return;
                }
                setConnection(null);
                setError('Failed to load the current SSO connection. Please refresh and try again.');
            } finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
                setLoading(false);
            }
        })();

        return () => {
            controller.abort();
        };
    }, [dispatch]);

    const deleteExisting = useCallback(async (): Promise<boolean> => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setDeleting(true);
        setError(null);

        try {
            const response = await dispatch(
                deleteConnection({signal: controller.signal}),
            );

            if (controller.signal.aborted) {
                return false;
            }

            if (response.payload?.success) {
                setConnection(null);
                return true;
            }
            setError('Failed to delete SSO connection. Please try again.');
            return false;
        } catch (err) {
            if ((err as {name?: string})?.name === 'AbortError') {
                return false;
            }
            setError('Failed to reach the server. Please check your connection and try again.');
            return false;
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setDeleting(false);
        }
    }, [dispatch]);

    return {loading, error, connection, deleteExisting, deleting};
}
